import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { AppState, Page, Plan, Position } from '../types';
import { id } from '../lib/domain';
import { useWorkspace } from '../lib/useWorkspace';
import {
  customerWorkspaceState,
  mergeCustomerWorkspace,
  type CustomerWorkspace,
} from '../lib/customer-workspace';
import { isProfessionalAccount, isProfessionalView, shouldShowAdvanced } from '../lib/view-mode';
import { getEstateRole, loadCloudState, saveCloudState } from '../lib/cloud';
import { downloadState } from '../lib/export';
import { recordAdvisorVisit } from '../lib/admin-activity';
import { configured } from '../lib/supabase';
import { freeLocalStorageSpace } from '../lib/storage';
import { useAuth } from './AuthContext';
import type { EstateDashboardMode } from '../components/workspace/EstateDashboardModeSwitcher';

// Sand i alle fejltilfælde: tidligt (kladden kunne ikke sikkerhedskopieres, eller data ændrede sig under
// hentningen, så den ligger stadig åben) og sent (serverversionen er i brug, men kvitteringen kunne ikke
// gemmes, så kladden ligger i sikkerhedskopien). Må ikke love, at intet er ændret.
const SERVER_VERSION_NOT_LOADED =
  'Serverens version er ikke bekræftet indlæst. Dine lokale ændringer er ikke slettet: de er enten stadig åbne på denne enhed eller gemt som sikkerhedskopi i browseren. Download din version, frigør plads i browseren og prøv igen.';

export interface WorkspaceContextValue {
  // Tilstande & ændringsfunktioner
  state: AppState;
  fullState: AppState;
  setState: Dispatch<SetStateAction<AppState>>;
  replaceState: (update: SetStateAction<AppState>) => void;
  replaceCustomerState: (update: SetStateAction<AppState>) => void;

  // Revir og markudvælgelse
  selectedEstateId: string;
  chooseEstate: (estateId: string, allowedEstateIds?: string[]) => void;
  selectedFieldId: string;
  setSelectedFieldId: Dispatch<SetStateAction<string>>;
  isEditingShape: boolean;
  setIsEditingShape: Dispatch<SetStateAction<boolean>>;

  // Rettigheder og visningstilstande
  canEdit: boolean;
  readOnly: boolean;
  planReadOnly: boolean;
  isProView: boolean;
  showAdvanced: boolean;
  advanced: boolean;
  setAdvanced: Dispatch<SetStateAction<boolean>>;
  proMode: 'professional' | 'simple';
  setProMode: Dispatch<SetStateAction<'professional' | 'simple'>>;
  estateDashboardMode: EstateDashboardMode;
  setEstateDashboardMode: Dispatch<SetStateAction<EstateDashboardMode>>;

  // Lagring & Cloud synkronisering
  saveStatus: 'saved' | 'saving' | 'error';
  storageError: string | null;
  generation: number;
  revision: number;
  cloudDirty: boolean;
  cloudConflict: { local: AppState; remote: AppState; backupError?: string } | null;
  cloudError: string;
  setCloudError: Dispatch<SetStateAction<string>>;
  cloudBusy: boolean;
  cloudGeneration: number | null;
  setCloudGeneration: Dispatch<SetStateAction<number | null>>;
  reconcileCloud: (state: AppState, generation: number) => boolean;
  recoverCloud: (state: AppState, generation: number, revision: number) => boolean;
  acknowledgeCloud: (state: AppState, generation: number) => boolean;
  saveAccount: () => Promise<void>;
  reloadAccount: (discardDirty?: boolean) => Promise<void>;

  // Kundearbejdsplads for rådgivere
  customerWorkspace: CustomerWorkspace | null;
  setCustomerWorkspace: Dispatch<SetStateAction<CustomerWorkspace | null>>;
  openCustomer: (
    customer: CustomerWorkspace,
    estateId: string,
    estateMeta?: { id: string; name: string; address?: string; center?: [number, number] },
    targetPage?: Page,
  ) => void;
  leaveCustomer: () => void;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export interface WorkspaceProviderProps {
  demo?: boolean | { page?: Page; basket?: boolean };
  onNavigate?: (page: Page) => void;
  notify?: (message: string) => void;
  enteredWorkspace?: boolean;
  children: ReactNode;
}

export function WorkspaceProvider({
  demo,
  onNavigate,
  notify,
  enteredWorkspace = false,
  children,
}: WorkspaceProviderProps) {
  const isDemo = Boolean(demo);
  const workspace = useWorkspace(isDemo);
  const currentWorkspace = useRef(workspace);
  currentWorkspace.current = workspace;

  const { user, isAdmin, accountType, accountReady, subscriptionTier } = useAuth();
  const isProfessional = accountType === 'professional';
  const isFreeAccount =
    (subscriptionTier
      ? subscriptionTier === 'free'
      : !isProfessionalAccount({ accountType, isAdmin })) && !isDemo;

  // Mark- og figurvalg
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [isEditingShape, setIsEditingShape] = useState<boolean>(false);

  // Visnings- og rådgivertilstande
  const [advanced, setAdvanced] = useState<boolean>(false);
  const [proMode, setProMode] = useState<'professional' | 'simple'>(() => {
    try {
      const saved = localStorage.getItem('plotwise.proMode');
      return saved === 'simple' ? 'simple' : 'professional';
    } catch {
      return 'professional';
    }
  });

  const [estateDashboardMode, setEstateDashboardMode] = useState<EstateDashboardMode>(() => {
    if (!isAdmin) {
      return subscriptionTier === 'pro'
        ? 'pro'
        : subscriptionTier === 'semipro'
          ? 'semipro'
          : 'simple';
    }
    try {
      const saved = localStorage.getItem('plotwise.estateDashboardMode');
      if (saved === 'simple' || saved === 'semipro' || saved === 'pro') return saved;
    } catch {}
    return 'pro';
  });

  useEffect(() => {
    if (!isAdmin) {
      const targetMode: EstateDashboardMode =
        subscriptionTier === 'pro' ? 'pro' : subscriptionTier === 'semipro' ? 'semipro' : 'simple';
      setEstateDashboardMode(targetMode);
    }
  }, [isAdmin, subscriptionTier]);

  // Cloud status og fejl
  const [cloudError, setCloudError] = useState<string>('');
  const [cloudBusy, setCloudBusy] = useState<boolean>(false);
  const [cloudGeneration, setCloudGeneration] = useState<number | null>(null);
  const [cloudRetry, setCloudRetry] = useState<number>(0);

  // Kundearbejdsplads
  const [customerWorkspace, setCustomerWorkspace] = useState<CustomerWorkspace | null>(null);
  const activeCustomer = useRef(customerWorkspace);
  activeCustomer.current = customerWorkspace;
  const ownEstateIdRef = useRef<string>('');
  useEffect(() => {
    if (!customerWorkspace && workspace.state.selectedEstateId) {
      ownEstateIdRef.current = workspace.state.selectedEstateId;
    }
  }, [customerWorkspace, workspace.state.selectedEstateId]);

  const visibleState = useMemo(
    () =>
      customerWorkspace
        ? customerWorkspaceState(workspace.state, customerWorkspace.estateIds)
        : workspace.state,
    [workspace.state, customerWorkspace],
  );

  const [readOnly, setReadOnly] = useState<boolean>(false);
  useEffect(() => {
    setReadOnly(
      !isDemo && configured && getEstateRole(workspace.state.selectedEstateId) === 'viewer',
    );
  }, [workspace.state.selectedEstateId, isDemo]);

  const plan = visibleState.plans.find((p) => p.id === visibleState.selectedPlanId);
  const planReadOnly = readOnly || plan?.status === 'ready';
  const canEdit = !planReadOnly;

  const administration = isAdmin && !customerWorkspace;
  const isProView = isProfessionalView({ accountType, isAdmin, proMode });
  const showAdvanced = shouldShowAdvanced({
    accountType,
    isAdmin,
    proMode,
    advanced,
    hasAdvisorRole: getEstateRole(workspace.state.selectedEstateId) === 'advisor',
    isAdministration: administration,
    isDemo: isDemo || (!user && enteredWorkspace),
  });

  // Initial sky-afstemning ved login/skift af bruger
  useEffect(() => {
    setCloudGeneration(null);
    setCloudError('');
    setCloudBusy(false);
    setAdvanced(false);
    if (!user || !configured) {
      return;
    }
    const start = currentWorkspace.current;
    if (start.storageError) {
      setCloudError(start.storageError);
      return;
    }
    let cancelled = false;
    setCloudBusy(true);
    void loadCloudState(start.state)
      .then((cloudData) => {
        if (cancelled) return;
        if (!workspace.reconcileCloud(cloudData, start.generation)) {
          // Native server-authority: Adopter altid serverversionen, men først når den lokale kladde
          // er sikkerhedskopieret. Lykkes det ikke, vises fejlen tydeligt, og kladden slettes aldrig.
          if (
            !workspace.recoverCloud(cloudData, start.generation, currentWorkspace.current.revision)
          ) {
            setCloudError(SERVER_VERSION_NOT_LOADED);
            return;
          }
          setCloudGeneration(start.generation);
          setCloudError('');
          return;
        }
        setCloudGeneration(start.generation);
      })
      .catch((error) => {
        if (!cancelled)
          setCloudError(
            error instanceof Error
              ? error.message
              : 'Kontoen kunne ikke hentes. Dine lokale data er bevaret.',
          );
      })
      .finally(() => {
        if (!cancelled) setCloudBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspace.generation, cloudRetry, user?.id]);

  // Automatisk genforsøg ved cloud fejl
  useEffect(() => {
    if (!cloudError || !user || workspace.storageError) return;
    if (cloudError.includes('Samme oplysninger')) return;
    const timer = window.setTimeout(() => setCloudRetry((value) => value + 1), 15000);
    return () => window.clearTimeout(timer);
  }, [cloudError, user?.id, workspace.storageError]);

  // Opret standardrevir for gratis konto når cloud er indlæst
  useEffect(() => {
    if (
      !isFreeAccount ||
      !accountReady ||
      !user ||
      cloudGeneration !== workspace.generation ||
      cloudBusy ||
      cloudError ||
      workspace.state.estates.length
    )
      return;
    const estateId = id();
    const planId = id();
    const year = new Date().getFullYear();
    const defaultEstate = {
      id: estateId,
      name: 'Mit Revir',
      address: '',
      center: [11.82, 55.4] as Position,
      createdAt: new Date().toISOString(),
    };
    const defaultPlan: Plan = {
      id: planId,
      estateId,
      name: `Plejeplan ${year}`,
      year,
      status: 'draft' as const,
      notes: '',
      selections: {},
      updatedAt: new Date().toISOString(),
    };
    workspace.setState((s) => ({
      ...s,
      estates: [defaultEstate],
      plans: [defaultPlan],
      selectedEstateId: estateId,
      selectedPlanId: planId,
    }));
  }, [
    isFreeAccount,
    accountReady,
    user?.id,
    workspace.generation,
    cloudBusy,
    cloudError,
    workspace.state.estates.length,
    cloudGeneration,
    workspace,
  ]);

  // Automatisk gemning til skyen (debounced 2000ms)
  useEffect(() => {
    if (
      isDemo ||
      !configured ||
      !user ||
      workspace.storageError ||
      !workspace.cloudDirty ||
      cloudBusy ||
      cloudGeneration !== workspace.generation ||
      cloudError
    )
      return;
    const timer = window.setTimeout(() => {
      const start = currentWorkspace.current;
      setCloudBusy(true);
      void saveCloudState(start.state)
        .then(() => {
          if (currentWorkspace.current.generation === start.generation) {
            workspace.acknowledgeCloud(start.state, start.generation);
            setCloudError('');
          }
        })
        .catch((error) => {
          if (currentWorkspace.current.generation === start.generation) {
            setCloudError(
              error instanceof Error
                ? error.message
                : 'Ændringerne kunne ikke gemmes på kontoen. De er bevaret lokalt.',
            );
          }
        })
        .finally(() => {
          setCloudBusy(false);
        });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [
    workspace.state,
    workspace.generation,
    workspace.cloudDirty,
    isDemo,
    user?.id,
    workspace.storageError,
    cloudBusy,
    cloudGeneration,
    cloudError,
    workspace,
  ]);

  // Skift revir
  const chooseEstate = useCallback(
    (estateId: string, allowedEstateIds?: string[]) => {
      const allowed = allowedEstateIds ?? customerWorkspace?.estateIds;
      if (allowed && !allowed.includes(estateId)) return;
      workspace.setState((s) => {
        let planId = s.plans.find((p) => p.estateId === estateId)?.id ?? '';
        let plans = s.plans;
        if (!planId && estateId) {
          const year = new Date().getFullYear();
          const newPlan: Plan = {
            id: id(),
            estateId,
            name: `Plejeplan ${year}`,
            year,
            status: 'draft',
            notes: '',
            selections: {},
            updatedAt: new Date().toISOString(),
          };
          plans = [...s.plans, newPlan];
          planId = newPlan.id;
        }
        return {
          ...s,
          plans,
          selectedEstateId: estateId,
          selectedPlanId: planId,
        };
      });
    },
    [customerWorkspace?.estateIds, workspace],
  );

  // Erstat tilstand generelt
  const replaceState = useCallback(
    (update: SetStateAction<AppState>) => {
      if (typeof update !== 'function') {
        setSelectedFieldId('');
      }
      workspace.setState(update);
    },
    [workspace],
  );

  // Erstat tilstand inden for kundearbejdspladsen
  const replaceCustomerState = useCallback(
    (update: SetStateAction<AppState>) => {
      const customer = customerWorkspace;
      if (!customer || activeCustomer.current !== customer) return;
      workspace.setState((complete) => {
        if (activeCustomer.current !== customer) return complete;
        const previous = customerWorkspaceState(complete, customer.estateIds);
        const next = typeof update === 'function' ? update(previous) : update;
        try {
          return mergeCustomerWorkspace(complete, next, customer.estateIds);
        } catch (error) {
          if (notify) {
            notify(error instanceof Error ? error.message : 'Kundens ændring kunne ikke gemmes.');
          }
          return complete;
        }
      });
    },
    [customerWorkspace, workspace, notify],
  );

  // Smart dispatcher: dirigerer til kunden hvis åben, ellers normalt
  const setState = useCallback<Dispatch<SetStateAction<AppState>>>(
    (action) => {
      if (customerWorkspace) {
        replaceCustomerState(action);
      } else {
        replaceState(action);
      }
    },
    [customerWorkspace, replaceCustomerState, replaceState],
  );

  // Gem til skyen manuelt
  const saveAccount = useCallback(async () => {
    if (isDemo || !user) {
      if (isDemo && notify) notify('Rundtur: ændringerne gemmes kun midlertidigt.');
      return;
    }
    const start = currentWorkspace.current;
    if (cloudGeneration !== start.generation)
      throw new Error(
        'Kontoens version kontrolleres før gemning. Dine ændringer er bevaret; prøv igen om lidt.',
      );
    await saveCloudState(start.state);
    if (currentWorkspace.current.generation !== start.generation) return;
    workspace.acknowledgeCloud(start.state, start.generation);
    setCloudError('');
  }, [isDemo, user, cloudGeneration, notify, workspace]);

  // Genindlæs fra skyen manuelt
  const reloadAccount = useCallback(
    async (discardDirty = false) => {
      if (isDemo || !user) {
        if (isDemo && notify) notify('Rundtur: der hentes ingen kontodata.');
        return;
      }
      const start = currentWorkspace.current;
      if (start.storageError) throw new Error(start.storageError);
      if (discardDirty && !isAdmin) downloadState(start.state, 'plotwise-foer-cloudhentning');
      if (discardDirty && user?.id) freeLocalStorageSpace(user.id);
      const next = await loadCloudState(start.state);
      if (currentWorkspace.current.generation !== start.generation) return;
      const applied =
        discardDirty || isAdmin
          ? workspace.recoverCloud(next, start.generation, start.revision)
          : workspace.reconcileCloud(next, start.generation) ||
            workspace.recoverCloud(next, start.generation, start.revision);
      if (!applied) {
        setCloudGeneration(null);
        throw new Error(SERVER_VERSION_NOT_LOADED);
      }
      setCloudGeneration(start.generation);
      setCloudError('');
      setSelectedFieldId('');
    },
    [isDemo, user, isAdmin, notify, workspace],
  );

  // Åbn kundens arbejdsplads med race condition beskyttelse
  const openCustomer = useCallback(
    (
      customer: CustomerWorkspace,
      estateId: string,
      estateMeta?: { id: string; name: string; address?: string; center?: [number, number] },
      targetPage: Page = 'customer-hub',
    ) => {
      if (!isAdmin && !isProfessional) return;
      const currentState = currentWorkspace.current.state;
      let targetEstateId = estateId;
      if (!targetEstateId) {
        const clientEstate = currentState.estates.find(
          (e) => customer.estateIds.includes(e.id) || e.ownerUserId === customer.userId,
        );
        if (clientEstate) targetEstateId = clientEstate.id;
      }
      if (!targetEstateId && estateMeta?.id) {
        targetEstateId = estateMeta.id;
      }
      if (!targetEstateId) {
        targetEstateId = id();
      }

      let targetEstate = currentState.estates.find((estate) => estate.id === targetEstateId);

      if (!targetEstate) {
        targetEstate = {
          id: targetEstateId,
          name: estateMeta?.name || `${customer.name}s revir`,
          address: estateMeta?.address || 'Bregentvedvej 1, 4690 Haslev',
          center: estateMeta?.center || [12.012, 55.352],
          createdAt: new Date().toISOString(),
          ownerUserId: customer.userId,
        };
      } else {
        targetEstate = {
          ...targetEstate,
          ...(customer.userId && !targetEstate.ownerUserId ? { ownerUserId: customer.userId } : {}),
          ...(estateMeta?.address &&
          (!targetEstate.address || targetEstate.address === 'Bregentvedvej 1, 4690 Haslev')
            ? { address: estateMeta.address, center: estateMeta.center || targetEstate.center }
            : {}),
          ...(estateMeta?.name &&
          (!targetEstate.name || targetEstate.name === `${customer.name}s revir`)
            ? { name: estateMeta.name }
            : {}),
        };
      }

      const year = new Date().getFullYear();
      const existingPlan = currentState.plans.find((p) => p.estateId === targetEstateId);
      const planId = existingPlan?.id || id();
      const autoPlan: Plan = existingPlan || {
        id: planId,
        estateId: targetEstateId,
        name: `Plejeplan ${year}`,
        year,
        status: 'draft',
        notes: '',
        selections: {},
        updatedAt: new Date().toISOString(),
      };

      const matchingIds = customer.estateIds.includes(targetEstateId)
        ? customer.estateIds
        : [targetEstateId, ...customer.estateIds];

      workspace.setState((s) => {
        const estates =
          targetEstate && !s.estates.some((e) => e.id === targetEstateId)
            ? [...s.estates, targetEstate]
            : s.estates.map((e) => (e.id === targetEstateId ? { ...e, ...targetEstate } : e));
        const plans = !s.plans.some((p) => p.estateId === targetEstateId)
          ? [...s.plans, autoPlan]
          : s.plans;
        return {
          ...s,
          estates,
          plans,
          selectedEstateId: targetEstateId,
          selectedPlanId: planId,
        };
      });

      setCustomerWorkspace({
        ...customer,
        estateIds: matchingIds,
      });

      recordAdvisorVisit(
        customer.userId,
        user?.email || 'Rådgiver Emil Beiner',
        user?.email || '',
        user?.id,
      );
      const targetMode: EstateDashboardMode =
        customer.tier === 'pro' ? 'pro' : customer.tier === 'semipro' ? 'semipro' : 'simple';
      setEstateDashboardMode(targetMode);
      try {
        localStorage.setItem('plotwise.estateDashboardMode', targetMode);
      } catch {}
      setAdvanced(false);
      onNavigate?.(targetPage);

      // Løsning af race condition i openCustomer:
      // Capture expectedGeneration og expectedRevision FØR det asynkrone netværkskald
      const expectedGeneration = currentWorkspace.current.generation;
      const expectedRevision = currentWorkspace.current.revision;

      void loadCloudState(currentState)
        .then((cloudData) => {
          // Tjek om der er sket lokale ændringer i mellemtiden, eller kunden er forladt
          if (
            currentWorkspace.current.generation !== expectedGeneration ||
            currentWorkspace.current.revision !== expectedRevision ||
            activeCustomer.current?.userId !== customer.userId
          ) {
            // Brugeren/rådgiveren har allerede lavet ændringer (f.eks. tegnet en mark) —
            // overskriv IKKE nytegnet data med forældet netværksdata.
            return;
          }
          if (!workspace.recoverCloud(cloudData, expectedGeneration, expectedRevision)) {
            // Ingen falsk succes: kundens version er ikke bekræftet indlæst, og kladden slettes aldrig.
            notify?.(SERVER_VERSION_NOT_LOADED);
            return;
          }
          setCloudGeneration(expectedGeneration);
          setCloudError('');
        })
        .catch(() => {});
    },
    [isAdmin, isProfessional, workspace, user, onNavigate, notify],
  );

  // Forlad kundens arbejdsplads
  const leaveCustomer = useCallback(() => {
    setCustomerWorkspace(null);
    setAdvanced(false);
    setSelectedFieldId('');
    if (ownEstateIdRef.current) {
      chooseEstate(ownEstateIdRef.current);
    }
    onNavigate?.(isAdmin ? 'superadmin' : isProfessional ? 'clients' : 'overview');
  }, [isAdmin, isProfessional, chooseEstate, setSelectedFieldId, onNavigate]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      state: visibleState,
      fullState: workspace.state,
      setState,
      replaceState,
      replaceCustomerState,
      selectedEstateId: visibleState.selectedEstateId,
      chooseEstate,
      selectedFieldId,
      setSelectedFieldId,
      isEditingShape,
      setIsEditingShape,
      canEdit,
      readOnly,
      planReadOnly,
      isProView,
      showAdvanced,
      advanced,
      setAdvanced,
      proMode,
      setProMode,
      estateDashboardMode,
      setEstateDashboardMode,
      saveStatus: workspace.saveStatus,
      storageError: workspace.storageError,
      generation: workspace.generation,
      revision: workspace.revision,
      cloudDirty: workspace.cloudDirty,
      cloudConflict: workspace.cloudConflict,
      cloudError,
      setCloudError,
      cloudBusy,
      cloudGeneration,
      setCloudGeneration,
      reconcileCloud: workspace.reconcileCloud,
      recoverCloud: workspace.recoverCloud,
      acknowledgeCloud: workspace.acknowledgeCloud,
      saveAccount,
      reloadAccount,
      customerWorkspace,
      setCustomerWorkspace,
      openCustomer,
      leaveCustomer,
    }),
    [
      visibleState,
      workspace.state,
      workspace.saveStatus,
      workspace.storageError,
      workspace.generation,
      workspace.revision,
      workspace.cloudDirty,
      workspace.cloudConflict,
      cloudError,
      cloudBusy,
      cloudGeneration,
      workspace.reconcileCloud,
      workspace.recoverCloud,
      workspace.acknowledgeCloud,
      saveAccount,
      reloadAccount,
      setState,
      replaceState,
      replaceCustomerState,
      chooseEstate,
      selectedFieldId,
      isEditingShape,
      canEdit,
      readOnly,
      planReadOnly,
      isProView,
      showAdvanced,
      advanced,
      proMode,
      estateDashboardMode,
      customerWorkspace,
      openCustomer,
      leaveCustomer,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaceContext skal anvendes inden i en WorkspaceProvider');
  }
  return context;
}
